// ✅ FILE: src/pages/attendance/Attendance.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import LoadingScreen from "../Utils/LoadingScreen";
import "./attendance.css";

const STATUS_ORDER = ["full_day", "half_day", "absent"];
const STATUS_LABEL = { full_day: "F", half_day: "H", absent: "A" };

const STATUS_CLASS = {
  full_day: "bg-emerald-500 text-white",
  half_day: "bg-amber-400 text-slate-900",
  absent: "bg-rose-500 text-white",
};

const TYPE_BADGE = {
  full_time: "bg-indigo-500/10 text-indigo-700 border-indigo-200",
  part_time: "bg-sky-500/10 text-sky-700 border-sky-200",
  normal: "bg-slate-500/10 text-slate-700 border-slate-200",
};

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const getMonthDays = (year, month) => {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) => toYMD(new Date(year, month, i + 1)));
};

const formatDay = (ymd) => new Date(ymd).getDate();

export default function Attendance() {
  const { baseurl, token, AdminId, setIsUnauthorised } = useGlobalContext();

  // month selection
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-index
  const dates = useMemo(() => getMonthDays(year, month), [year, month]);

  // drivers list (for dropdown)
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverListLoading, setDriverListLoading] = useState(false);

  // UI inputs (not applied until Apply Filters)
  const [uiDriverCode, setUiDriverCode] = useState("");
  const [uiSearch, setUiSearch] = useState("");
  const [uiType, setUiType] = useState("all");
  const [uiStatus, setUiStatus] = useState("all");

  // applied filters
  const [applied, setApplied] = useState({
    driverCode: "",
    search: "",
    type: "all",
    status: "all",
  });

  // data
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [dirtyMap, setDirtyMap] = useState({}); // key = driver|date -> {driverCode,date,status}

  const canCallApi = !!baseurl && !!token && !!AdminId;

  /* ===================== FETCH DRIVER LIST ===================== */
  const fetchDriverList = async () => {
    if (!canCallApi) return;

    setDriverListLoading(true);
    try {
      const payload = { userid: "", adminId: String(AdminId) };
      const res = await axios.post(`${baseurl}/api/Driver/v1.0/getDriverList`, payload, {
        headers: { Authorization: `Basic ${token}` },
      });

      if (res?.data?.responseCode === "00") {
        setDriverOptions(res.data.data || []);
      } else if (res?.data?.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to fetch drivers");
      }
    } catch (e) {
      toast.error("Error fetching driver list");
    } finally {
      setDriverListLoading(false);
    }
  };

  /* ✅ IMPORTANT FIX: depend on AdminId/token/baseurl so it runs once AdminId is ready */
  useEffect(() => {
    if (!canCallApi) return;
    fetchDriverList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AdminId, baseurl, token]);

  /* ===================== FETCH ATTENDANCE ===================== */
  const fetchAttendance = async (driverCodeParam) => {
    if (!canCallApi) return;

    setLoading(true);
    setDirtyMap({});

    try {
      const payload = {
        adminId: String(AdminId),
        driverCode: driverCodeParam || "",
        fromDate: dates[0],
        toDate: dates[dates.length - 1],
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getDriverAttendanceRecord`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res?.data?.responseCode === "401") {
        setIsUnauthorised?.(true);
        return;
      }

      if (res?.data?.responseCode !== "00") {
        toast.error(res?.data?.responseMessage || "Failed to fetch attendance");
        setDrivers([]);
        return;
      }

      const normalized = (res.data.data || []).map((d) => {
        const map = {};
        (d.attendance || []).forEach((a) => {
          if (a?.date && a?.status) map[a.date] = a.status; // last wins if duplicate
        });

        dates.forEach((dt) => {
          if (!map[dt]) map[dt] = "absent";
        });

        return { ...d, attendanceMap: map };
      });

      setDrivers(normalized);
    } catch (e) {
      toast.error("Error fetching attendance");
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  /* ✅ IMPORTANT FIX: include AdminId so it refetches when AdminId becomes available */
  useEffect(() => {
    // if (!canCallApi) return;
    fetchAttendance(applied.driverCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AdminId, year, month]);

  /* ===================== APPLY FILTERS ===================== */
  const onApplyFilters = () => {
    const next = {
      driverCode: uiDriverCode,
      search: uiSearch.trim(),
      type: uiType,
      status: uiStatus,
    };
    setApplied(next);
    fetchAttendance(next.driverCode);
  };

  const onResetFilters = () => {
    setUiDriverCode("");
    setUiSearch("");
    setUiType("all");
    setUiStatus("all");
    const next = { driverCode: "", search: "", type: "all", status: "all" };
    setApplied(next);
    fetchAttendance("");
  };

  /* ===================== GRID INTERACTION ===================== */
  const cycleStatus = (driverCode, date, current) => {
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];
    const key = `${driverCode}|${date}`;

    setDrivers((prev) =>
      prev.map((d) =>
        d.driverCode === driverCode
          ? { ...d, attendanceMap: { ...d.attendanceMap, [date]: next } }
          : d
      )
    );

    setDirtyMap((prev) => ({ ...prev, [key]: { driverCode, date, status: next } }));
  };

  /* ===================== SAVE (Batch Update API) ===================== */
  const saveAll = async () => {
    const data = Object.values(dirtyMap);
    if (!data.length) return toast.message("No changes to save");
    if (!canCallApi) return toast.error("Admin/session not ready");

    setLoading(true);
    try {
      const payload = { adminId: String(AdminId), data };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/updateDriverAttendanceRecord`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res?.data?.responseCode === "401") {
        setIsUnauthorised?.(true);
        return;
      }

      if (res?.data?.responseCode === "00") {
        toast.success(res.data.responseMessage || "Attendance updated");
        setDirtyMap({});
      } else {
        toast.error(res?.data?.responseMessage || "Update failed");
      }
    } catch (e) {
      toast.error("Error updating attendance");
    } finally {
      setLoading(false);
    }
  };

  const discardAll = () => {
    if (!Object.keys(dirtyMap).length) return;
    setDirtyMap({});
    fetchAttendance(applied.driverCode);
  };

  /* ===================== CLIENT-SIDE FILTERING (Applied only) ===================== */
  const filteredDrivers = useMemo(() => {
    let list = drivers;

    if (applied.type !== "all") {
      list = list.filter((d) => String(d.type || "").toLowerCase() === applied.type);
    }

    if (applied.search) {
      const q = applied.search.toLowerCase();
      list = list.filter((d) => {
        const name = String(d.name || "").toLowerCase();
        const code = String(d.driverCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }

    if (applied.status !== "all") {
      list = list.filter((d) =>
        Object.values(d.attendanceMap || {}).some((s) => s === applied.status)
      );
    }

    return list;
  }, [drivers, applied]);

  const dirtyCount = Object.keys(dirtyMap).length;

  return (
    <div className="w-full overflow-x-hidden px-4 py-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Attendance</h1>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span className="att-legend bg-emerald-500" /> F – Full
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-amber-400" /> H – Half
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-rose-500" /> A – Absent
            </div>
            <span className="text-slate-400">• Click badge to change (F → H → A)</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={discardAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            Discard ({dirtyCount})
          </button>

          <button
            onClick={saveAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            Save Changes ({dirtyCount})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 ">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Month</label>
              <input
                type="month"
                value={`${year}-${String(month + 1).padStart(2, "0")}`}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-");
                  setYear(+y);
                  setMonth(+m - 1);
                }}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-4">
              <label className="text-xs text-slate-500">Driver</label>
              <select
                value={uiDriverCode}
                onChange={(e) => setUiDriverCode(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                disabled={driverListLoading}
              >
                <option value="">All Drivers</option>
                {driverOptions.map((d) => (
                  <option key={d.driverId} value={d.driverId}>
                    {d.fullName || d.username || d.driverId} ({d.driverId})
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs text-slate-500">Search</label>
              <input
                value={uiSearch}
                onChange={(e) => setUiSearch(e.target.value)}
                placeholder="Search name or code..."
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Type</label>
              <select
                value={uiType}
                onChange={(e) => setUiType(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">All</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Status</label>
              <select
                value={uiStatus}
                onChange={(e) => setUiStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">Any</option>
                <option value="full_day">Full</option>
                <option value="half_day">Half</option>
                <option value="absent">Absent</option>
              </select>
            </div>

            <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 mt-2">
              <div className="flex gap-2">
                <button
                  onClick={onApplyFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                >
                  Apply Filters
                </button>

                <button
                  onClick={onResetFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                >
                  Reset
                </button>
              </div>

              <div className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">{filteredDrivers.length}</span>{" "}
                drivers • <span className="font-semibold text-slate-700">{dates.length}</span> days
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-4">
          <LoadingScreen />
        </div>
      ) : null}

      {/* Grid */}
      <div className="bg-white rounded-2xl border border-slate-200 mt-4">
        <div className="attendance-grid-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th className="attendance-th sticky-top sticky-driver text-left">Partner</th>
                {dates.map((d) => (
                  <th key={d} className="bg-orange-400 attendance-th sticky-top text-center date-col">
                    {formatDay(d)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredDrivers.length === 0 ? (
                <tr>
                  <td colSpan={1 + dates.length} className="py-10 text-center text-slate-500">
                    No drivers found for this filter/month.
                  </td>
                </tr>
              ) : null}

              {filteredDrivers.map((d) => (
                <tr key={d.driverCode} className="hover:bg-slate-50/70">
                  <td className=" attendance-td sticky-driver">
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900 truncate max-w-[150px]">
                        {d.name || "-"}
                      </div>
                      <div className="text-[10px] text-slate-500">{d.driverCode}</div>
                      <span
                        className={[
                          "inline-flex items-center mt-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold",
                          TYPE_BADGE[String(d.type || "").toLowerCase()] || TYPE_BADGE.normal,
                        ].join(" ")}
                      >
                        {String(d.type || "normal").replaceAll("_", " ")}
                      </span>
                    </div>
                  </td>

                  {dates.map((date) => {
                    const status = d.attendanceMap?.[date] || "absent";
                    const key = `${d.driverCode}|${date}`;
                    const isDirty = !!dirtyMap[key];

                    return (
                      <td key={date} className=" attendance-td text-center">
                        <button
                          type="button"
                          onClick={() => cycleStatus(d.driverCode, date, status)}
                          className={[
                            "att-badge",
                            STATUS_CLASS[status],
                            isDirty ? "ring-2 ring-slate-900/20" : "",
                          ].join(" ")}
                          title="Click to change"
                        >
                          {STATUS_LABEL[status]}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            Driver column is fixed. Scroll inside grid to see all dates.
          </div>
          <div className="text-xs">
            {dirtyCount ? (
              <span className="text-rose-600 font-semibold">{dirtyCount} unsaved change(s)</span>
            ) : (
              <span className="text-emerald-600 font-semibold">All changes saved</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
